/**
 * Run-and-queue-latest serialization for an async job (Phase 2d-1,
 * 2026-09-29).
 *
 * Built for the dashboard badge-counter recompute: its trailing debounce
 * shapes SCHEDULING, but the recompute itself is async, so a run slower
 * than the debounce window could overlap the next one — no aggregate bound
 * existed. This wrapper adds EXECUTION serialization:
 *
 *   - trigger while idle        → run immediately
 *   - trigger while running     → set ONE pending flag (any number of
 *                                 triggers coalesce into it)
 *   - run settles, pending set  → run exactly once more
 *
 * Provable bound: at most one execution in flight and at most one queued,
 * ever. A failed run releases the runner (the error propagates to the
 * caller of trigger; the counters' recompute catches internally, so in
 * practice nothing throws here). cancel() drops any queued run and makes
 * further triggers no-ops — call it from the subscription's unsubscribe.
 *
 * Scope note (ledger): this bounds ONE subsystem's job. It is not a global
 * scheduler; independent subsystems (e.g. dashboard section loaders) remain
 * independently bounded by their own guards.
 */
export function createCoalescingRunner(run) {
  let inFlight = false;
  let pending = false;
  let cancelled = false;

  const trigger = async () => {
    if (cancelled) return;
    if (inFlight) {
      pending = true;
      return;
    }
    inFlight = true;
    try {
      do {
        pending = false;
        await run();
      } while (pending && !cancelled);
    } finally {
      inFlight = false;
    }
  };

  trigger.cancel = () => {
    cancelled = true;
    pending = false;
  };

  return trigger;
}
